import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, Loader2, TrendingUp, TrendingDown, 
  RefreshCw, Download, FileSpreadsheet, Calendar
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useLanguage } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function IncomeStatement() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exporting, setExporting] = useState(false);
  const [businessProfile, setBusinessProfile] = useState(null);
  const reportRef = useRef(null);

  useEffect(() => {
    // Only auto-load if not using custom range
    if (period !== "custom") {
      loadIncomeStatement();
    } else {
      setLoading(false);
    }
    loadBusinessProfile();
  }, [period]);

  const loadBusinessProfile = async () => {
    try {
      const response = await axios.get(`${API}/business-profile`);
      setBusinessProfile(response.data);
    } catch (error) {
      console.error("Failed to load business profile:", error);
    }
  };

  const loadIncomeStatement = async () => {
    setLoading(true);
    try {
      let url = `${API}/cash/summary?period=${period}`;
      if (period === "custom" && startDate && endDate) {
        url = `${API}/cash/summary?start_date=${startDate}&end_date=${endDate}`;
      }
      const response = await axios.get(url);
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load income statement");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const getPeriodLabel = () => {
    if (data?.start_date && data?.end_date) {
      return `${formatDate(data.start_date)} - ${formatDate(data.end_date)}`;
    }
    return period;
  };

  const exportToPDF = async () => {
    if (!reportRef.current || !data) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff"
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      pdf.save(`income-statement-${data.start_date}-to-${data.end_date}.pdf`);
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to export PDF");
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const exportToExcel = () => {
    if (!data) return;
    
    const rows = [
      ["Income Statement"],
      [`Period: ${formatDate(data.start_date)} to ${formatDate(data.end_date)}`],
      [""],
      ["REVENUE"],
      ...Object.entries(data.inflows_by_category || {}).map(([cat, amt]) => [cat, amt]),
      ["Total Revenue", data.total_inflows],
      [""],
      ["EXPENSES"],
      ...Object.entries(data.outflows_by_category || {}).map(([cat, amt]) => [cat, amt]),
      ["Total Expenses", data.total_outflows],
      [""],
      ["NET INCOME", data.net_cash_flow],
      [""],
      ["CASH FLOW SUMMARY"],
      ["Opening Balance", data.opening_balance],
      ["Total Inflows", data.total_inflows],
      ["Total Outflows", data.total_outflows],
      ["Net Cash Flow", data.net_cash_flow],
      ["Closing Balance", data.closing_balance]
    ];
    
    const csv = rows.map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `income-statement-${data.start_date}-to-${data.end_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Excel/CSV downloaded");
  };

  const incomeCategories = Object.entries(data?.inflows_by_category || {}).sort(([,a], [,b]) => b - a);
  const expenseCategories = Object.entries(data?.outflows_by_category || {}).sort(([,a], [,b]) => b - a);

  return (
    <div className="max-w-4xl mx-auto" data-testid="income-statement-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Link to="/cash-flow">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Income Statement</h1>
            <p className="text-sm text-muted-foreground">
              {data ? getPeriodLabel() : "Loading..."}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36 h-9">
              <Calendar className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          
          {period === "custom" && (
            <>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-36 h-9"
                data-testid="income-start-date"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-36 h-9"
                data-testid="income-end-date"
              />
              <Button 
                variant="default" 
                size="sm" 
                onClick={loadIncomeStatement} 
                disabled={loading || !startDate || !endDate} 
                className="h-9"
                data-testid="apply-custom-range-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
              </Button>
            </>
          )}
          
          <Button variant="ghost" size="sm" onClick={loadIncomeStatement} disabled={loading} className="h-9 w-9 p-0">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" size="sm" onClick={exportToPDF} disabled={exporting || !data} className="h-9">
            <Download className="w-4 h-4 mr-1.5" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={exportToExcel} disabled={!data} className="h-9">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" />
            Excel
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : data ? (
        <div ref={reportRef} className="space-y-8 bg-white p-6 rounded-lg">
          {/* Company Branding Header - For PDF Export */}
          <div className="flex items-start justify-between pb-6 border-b-2" style={{ borderColor: businessProfile?.primary_color || '#047857' }}>
            <div className="flex items-center gap-4">
              {(businessProfile?.logo_data || businessProfile?.logo_url) && (
                <img 
                  src={businessProfile.logo_data || businessProfile.logo_url}
                  alt="Company Logo"
                  className="h-12 w-auto max-w-[100px] object-contain"
                  crossOrigin="anonymous"
                />
              )}
              <div>
                <h1 className="text-xl font-bold" style={{ color: businessProfile?.primary_color || '#047857' }}>
                  {businessProfile?.business_name || 'Income Statement'}
                </h1>
                <p className="text-sm text-muted-foreground">Income Statement</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium">{getPeriodLabel()}</p>
              <p className="text-xs text-muted-foreground">Generated {new Date().toLocaleDateString()}</p>
            </div>
          </div>

          {/* Key Metrics Row */}
          <div className="flex items-baseline gap-12 pb-6 border-b border-border">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Total Revenue</p>
              <p className="text-3xl font-bold tabular-nums text-emerald-600">+{formatCurrency(data.total_inflows)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Total Expenses</p>
              <p className="text-3xl font-bold tabular-nums text-red-500">-{formatCurrency(data.total_outflows)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Net Income</p>
              <p className={`text-3xl font-bold tabular-nums ${data.net_cash_flow >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {formatCurrency(data.net_cash_flow)}
              </p>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid md:grid-cols-2 gap-12">
            {/* Revenue */}
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                Revenue
              </h2>
              
              {incomeCategories.length > 0 ? (
                <div className="space-y-3">
                  {incomeCategories.map(([category, amount]) => (
                    <div key={category} className="flex items-center justify-between py-2">
                      <span className="text-sm">{category}</span>
                      <span className="font-medium tabular-nums text-emerald-600">+{formatCurrency(amount)}</span>
                    </div>
                  ))}
                  
                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <span className="font-semibold">Total Revenue</span>
                    <span className="font-bold text-lg tabular-nums text-emerald-600">+{formatCurrency(data.total_inflows)}</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4">No revenue recorded</p>
              )}
            </div>

            {/* Expenses */}
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4 flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-red-500" />
                Expenses
              </h2>
              
              {expenseCategories.length > 0 ? (
                <div className="space-y-3">
                  {expenseCategories.map(([category, amount]) => (
                    <div key={category} className="flex items-center justify-between py-2">
                      <span className="text-sm">{category}</span>
                      <span className="font-medium tabular-nums text-red-500">-{formatCurrency(amount)}</span>
                    </div>
                  ))}
                  
                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <span className="font-semibold">Total Expenses</span>
                    <span className="font-bold text-lg tabular-nums text-red-500">-{formatCurrency(data.total_outflows)}</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4">No expenses recorded</p>
              )}
            </div>
          </div>

          {/* Net Income Summary */}
          <div className={`py-4 px-5 rounded-md ${data.net_cash_flow >= 0 ? 'bg-emerald-50 dark:bg-emerald-900/10' : 'bg-red-50 dark:bg-red-900/10'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">Net Income</p>
                <p className="text-sm text-muted-foreground">
                  Revenue minus Expenses
                </p>
              </div>
              <p className={`text-2xl font-bold tabular-nums ${data.net_cash_flow >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {formatCurrency(data.net_cash_flow)}
              </p>
            </div>
          </div>

          {/* Cash Position */}
          <div className="pt-6 border-t border-border">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">
              Cash Position
            </h2>
            
            <div className="grid grid-cols-3 gap-8">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Opening</p>
                <p className="text-lg font-semibold tabular-nums">{formatCurrency(data.opening_balance)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Net Change</p>
                <p className={`text-lg font-semibold tabular-nums ${data.net_cash_flow >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {data.net_cash_flow >= 0 ? '+' : ''}{formatCurrency(data.net_cash_flow)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Closing</p>
                <p className="text-lg font-semibold tabular-nums">{formatCurrency(data.closing_balance)}</p>
              </div>
            </div>
          </div>

          {/* Transaction Count */}
          <div className="flex items-center gap-6 text-sm text-muted-foreground pt-4 border-t border-border">
            <span>{data.inflow_count} income transactions</span>
            <span>{data.outflow_count} expense transactions</span>
          </div>

          {/* Footnote */}
          <p className="text-xs text-muted-foreground text-center pt-2">
            Cash-basis accounting · {formatDate(data.start_date)} to {formatDate(data.end_date)}
          </p>
        </div>
      ) : (
        <div className="text-center py-16">
          <p className="text-muted-foreground">Failed to load data</p>
          <Button variant="outline" onClick={loadIncomeStatement} className="mt-4">
            Try Again
          </Button>
        </div>
      )}
    </div>
  );
}
